import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import CustomDateTimePicker from "../../components/DateTimePicker/dateTimePicker";
import "../../components/DateTimePicker/dateTimePicker.css";
import "../../components/DateTimePicker/materialUIOverrides.css";
import moment from "moment";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek";
import {
  checkIfCommonTimeSlots,
  convertToScheduleFormat,
} from "../../components/CustomComponents/TimeSchedule/scheduleConv";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel";
import CategorySelector from "../../components/CustomComponents/modal/CategorySelector";
import DealSlotTypeSelectionModal from "../../components/CustomComponents/modal/DealSlotTypeSelectionModal";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import swal from "sweetalert";
import DealBranchAssignmentPanel from "../../components/CustomComponents/deal/DealBranchAssignmentPanel";
import useIsChildBranch from "../../hooks/useIsChildBranch";

const UpdateDeal = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const isChildBranch = useIsChildBranch();
  const [compCurrency, setCompCurrency] = useState("GBP");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modifiedBy, setModifiedBy] = useState("");
  const [categoryModalLoading, setCategoryModalLoading] = useState(false);

  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Variant options removed - users can now type any variant name

  const [formState, setFormState] = useState({
    dealID: 0,
    dealName: "",
    dealDescription: "",
    dealPrice: "",
    dealOrder: "0",
    minimumOrderValue: "",
    dealPricingType: "CustomPrice",
    dealDiscountPercentage: "",
    startDate: null,
    endDate: null,
  });

  const [toggles, setToggles] = useState({
    isActive: true,
    showInKiosk: true,
    showInPOS: true,
    showInOnline: true,
    autoDuplicateItem: false,
    isScheduleDeal: false,
  });

  const [imageState, setImageState] = useState({
    dealImage: "",
    imageFile: null,
  });

  const [slots, setSlots] = useState([]);
  const [expandedSlots, setExpandedSlots] = useState(new Set());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const selectedSlotRef = useRef(null); // Ref to preserve selectedSlot during async operations
  const selectedSlotIndexRef = useRef(null); // Ref to preserve selectedSlot index during async operations
  const typeSelectionInProgressRef = useRef(false); // Ref to track if type selection is in progress
  const formStateRef = useRef(formState); // Ref to always have latest formState
  const [showTypeSelectionModal, setShowTypeSelectionModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null); // 'categories' or 'items'
  const [allCategories, setAllCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [slotSaving, setSlotSaving] = useState(false);
  
  // Schedule state
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [schedules, setSchedules] = useState(schedule);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    { from: "00:00", to: "23:59" },
  ]);

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({ ...slot })),
    }));
    setSchedules(updatedSchedules);
  };

  useEffect(() => {
    if (profileData) {
      setCompCurrency(profileData?.compCurrency || "GBP");
      setModifiedBy(profileData?.userName || "");
    }
  }, [profileData?.compCurrency, profileData?.userName]);

  // Fetch all categories and items for selection
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: 'rootCat_Orderby',
          SortOrder: 'ASC',
          Search: '',
        }
      });
      if (response.data.status && response.data.data) {
        setAllCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [CompID]);

  const fetchItems = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_ONLY_PRODUCTS, {
        params: { CompID }
      });
      if (response.data.status && response.data.data) {
        setAllItems(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  }, [CompID]);

  useEffect(() => {
    fetchCategories();
    fetchItems();
  }, [fetchCategories, fetchItems]);

  const flattenCategories = useCallback((categories, acc = []) => {
    if (!Array.isArray(categories)) return acc;
    categories.forEach((cat) => {
      if (!cat) return;
      acc.push(cat);
      const children =
        cat.subCategoriesList ||
        cat.SubCategoriesList ||
        cat.subCategories ||
        cat.SubCategories ||
        cat.children ||
        cat.Children ||
        [];
      if (Array.isArray(children) && children.length > 0) {
        flattenCategories(children, acc);
      }
    });
    return acc;
  }, []);

  const getCategoryName = useCallback(
    (categoryID) => {
      const id = parseInt(categoryID, 10);
      if (!Number.isFinite(id)) return `Category ${categoryID}`;
      const flat = flattenCategories(allCategories);
      const category = flat.find((c) => {
        const cid = parseInt(c.rootCat_ID ?? c.RootCat_ID ?? c.id ?? c.ID, 10);
        return cid === id;
      });
      return (
        category?.rootCat_Name ||
        category?.RootCat_Name ||
        category?.name ||
        category?.Name ||
        `Category ${categoryID}`
      );
    },
    [allCategories, flattenCategories]
  );

  // Keep ref in sync with selectedSlot state
  useEffect(() => {
    selectedSlotRef.current = selectedSlot;
  }, [selectedSlot]);

  // Keep formStateRef in sync with formState
  useEffect(() => {
    formStateRef.current = formState;
  }, [formState]);

  // Child branches edit overrides only — redirect once profile is known
  const childRedirectDoneRef = useRef(false);
  useEffect(() => {
    if (childRedirectDoneRef.current) return;
    if (!dealId || !profileData) return;
    if (!isChildBranch) return;
    childRedirectDoneRef.current = true;
    navigate(`/deal/branchEditDeal/${dealId}`, { replace: true });
  }, [isChildBranch, dealId, navigate, profileData]);

  // Skip loading full editor if we already know this is a child branch
  const hasLoadedDealRef = useRef(false);

  const fetchDealData = useCallback(async () => {
    if (!dealId || !CompID) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_DEAL_BY_ID, {
        params: {
          DealID: dealId,
          CompID,
        },
      });
      console.log("Deal response:", response.data);

      if (response.data.status && response.data.data) {
        hasLoadedDealRef.current = true;
        const deal = response.data.data;
        const pricingType = (deal.pricingType || deal.PricingType || "CustomPrice").trim();
        // Only show dealPrice when pricing type is CustomPrice; only show discount when Discount (ignore stale DB values for the other type)
        const newFormState = {
          dealID: deal.dealID,
          dealName: deal.dealName || "",
          dealDescription: deal.dealDescription || "",
          dealPrice: pricingType === "CustomPrice" ? (deal.dealPrice != null && deal.dealPrice !== "" ? String(deal.dealPrice) : "") : "",
          dealOrder: deal.dealOrder?.toString() || "0",
          minimumOrderValue: deal.minimumOrderValue?.toString() || "",
          dealPricingType: pricingType,
          dealDiscountPercentage: pricingType === "Discount" ? (deal.discountPercentage != null ? String(deal.discountPercentage) : (deal.DiscountPercentage != null ? String(deal.DiscountPercentage) : "")) : "",
          startDate: deal.startDate ? moment(deal.startDate) : null,
          endDate: deal.endDate ? moment(deal.endDate) : null,
        };
        setFormState(newFormState);
        formStateRef.current = newFormState; // Update ref when fetching deal data

        setToggles({
          isActive: deal.isActive !== false,
          showInKiosk: deal.showInKiosk !== false,
          showInPOS: deal.showInPOS !== false,
          showInOnline: deal.showInOnline !== false,
          autoDuplicateItem: deal.autoDuplicateItem === true,
          isScheduleDeal: deal.isScheduleDeal === true,
        });

        // Handle weekly timings if schedule is enabled
        if (deal.isScheduleDeal === true && deal.dealWeeklyTimings && deal.dealWeeklyTimings.length > 0) {
          try {
            console.log("Processing deal weekly timings:", deal.dealWeeklyTimings);
            
            const weeklyTimings = deal.dealWeeklyTimings;
            
            // Check if all days have the same time slots
            const isCommon = checkIfCommonTimeSlots(weeklyTimings);
            console.log("Are time slots common across all days?", isCommon);
            
            if (isCommon && weeklyTimings.length > 0) {
              // All days have same time slots - use "same for all days" mode
              // The CommonTimeSlotForAllWeek component expects times in HH:mm format (24-hour)
              const firstDaySlots = weeklyTimings
                .filter((day) => day.dayName === weeklyTimings[0].dayName)
                .map((day) => {
                  console.log(`Time slot: ${day.openTime} to ${day.closeTime}`);
                  return {
                    from: day.openTime,  // Keep as HH:mm (24-hour format)
                    to: day.closeTime,   // Keep as HH:mm (24-hour format)
                  };
                });
              console.log("Common time slots (HH:mm format):", firstDaySlots);
              setCommonTimeSlot(firstDaySlots);
              setUseSameTimeSlots(true);
            } else {
              // Different time slots per day
              console.log("Using different time slots for each day");
              setUseSameTimeSlots(false);
            }
            
            // Convert and set schedules for individual day view
            // convertToScheduleFormat expects times in HH:mm format (24-hour)
            const convertedSchedules = convertToScheduleFormat(weeklyTimings);
            console.log("Converted schedules:", convertedSchedules);
            setSchedules(convertedSchedules);
          } catch (error) {
            console.error("Error processing deal weekly timings:", error);
            // Reset to defaults on error
            setSchedules(schedule);
            setCommonTimeSlot([{ from: "00:00", to: "23:59" }]);
            setUseSameTimeSlots(true);
          }
        } else {
          console.log("No schedule enabled or no weekly timings data");
          setSchedules(schedule);
          setCommonTimeSlot([{ from: "00:00", to: "23:59" }]);
          setUseSameTimeSlots(true);
        }

        // Construct full URL for image display if it's a relative path
        const baseURL = axiosInstance.defaults.baseURL || "";
        let imageDisplayUrl = deal.dealImage || "";
        if (imageDisplayUrl && !imageDisplayUrl.startsWith("http")) {
          // If it's a relative path, construct full URL for display
          const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
          const imagePath = imageDisplayUrl.startsWith("/") ? imageDisplayUrl : `/${imageDisplayUrl}`;
          imageDisplayUrl = `${baseUrlClean}${imagePath}`;
        }
        
        setImageState({
          dealImage: imageDisplayUrl,
          imageFile: null,
        });

        // Fetch slots with all related data (pass deal pricing type so slots default to NoDiscount when not SlotPricing)
        await fetchSlots(deal.dealID, null, deal.pricingType || deal.PricingType);
      } else {
        showErrorToast("Deal not found");
        // Only leave the page if we never successfully loaded this deal
        if (!hasLoadedDealRef.current) {
          navigate("/deal/manageDeals");
        }
      }
    } catch (error) {
      console.error("Error fetching deal:", error);
      showErrorToast("Failed to load deal");
      if (!hasLoadedDealRef.current) {
        navigate("/deal/manageDeals");
      }
    } finally {
      setLoading(false);
    }
  }, [dealId, CompID, navigate]);

  const fetchSlots = useCallback(async (dealID, keepExpandedSlotID = null, dealPricingType = null) => {
    try {
      const response = await axiosInstance.get(APIs.GET_DEAL_SLOTS_BY_DEAL_ID, {
        params: { DealID: dealID },
      });
      
      const isSlotPricing = (dealPricingType || "").trim() === "SlotPricing";
      if (response.data.status && response.data.data) {
        const slotsData = await Promise.all(
          response.data.data.map(async (slot) => {
            // Fetch allowed categories
            const categoriesRes = await axiosInstance.get(
              APIs.GET_DEAL_SLOT_ALLOWED_CATEGORIES,
              { params: { SlotID: slot.slotID } }
            );
            const categories = categoriesRes.data.status ? categoriesRes.data.data : [];
            console.log(`Categories for slot ${slot.slotID}:`, categories);
            
            // Normalize category data (handle both PascalCase and camelCase from API)
            const normalizedCategories = Array.isArray(categories) ? categories.map(cat => ({
              id: cat.id || cat.ID,
              slotID: cat.slotID || cat.SlotID,
              categoryID: cat.categoryID || cat.CategoryID,
              categoryName: cat.categoryName || cat.CategoryName || cat.rootCat_Name || cat.RootCat_Name || null,
              rootCat_Name: cat.categoryName || cat.CategoryName || cat.rootCat_Name || cat.RootCat_Name || null,
            })) : [];

            // Fetch allowed items
            const itemsRes = await axiosInstance.get(
              APIs.GET_DEAL_SLOT_ALLOWED_ITEMS,
              { params: { SlotID: slot.slotID } }
            );
            const items = itemsRes.data.status ? itemsRes.data.data : [];
            console.log(`Items for slot ${slot.slotID}:`, items);
            
            // Normalize item data (handle both PascalCase and camelCase from API)
            const normalizedItems = Array.isArray(items) ? items.map(item => ({
              id: item.id || item.ID,
              slotID: item.slotID || item.SlotID,
              // Robustly handle different API field names
              itemID: item.itemID || item.ItemID || item.menuItem_ID || item.MenuItem_ID || item.MenuItemID || item.MenuItemId || item.id || item.ID,
            })) : [];

            // Fetch constraints
            const constraintRes = await axiosInstance.get(
              APIs.GET_DEAL_SLOT_CONSTRAINT,
              { params: { SlotID: slot.slotID } }
            );
            const constraint = constraintRes.data.status ? constraintRes.data.data : null;

            // When deal pricing type is NOT SlotPricing, every slot is NoDiscount by default
            const slotPricingType = isSlotPricing ? (slot.pricingType || slot.PricingType || "NoDiscount") : "NoDiscount";
            return {
              ...slot,
              allowedCategories: normalizedCategories,
              allowedItems: normalizedItems,
              constraint: constraint,
              _isUnsaved: false,
              buyQuantity: slot.buyQuantity !== undefined && slot.buyQuantity !== null ? slot.buyQuantity : 0,
              pricingType: slotPricingType,
              discountPercentage: isSlotPricing ? (slot.discountPercentage ?? slot.DiscountPercentage ?? null) : null,
              isFree: isSlotPricing ? (slot.isFree || slot.IsFree || false) : false,
              customPrice: isSlotPricing ? (slot.customPrice ?? slot.CustomPrice ?? null) : null,
              autoDuplicateItem: slot.autoDuplicateItem ?? slot.AutoDuplicateItem ?? false,
            };
          })
        );
        console.log("All slots data after normalization:", slotsData);
        setSlots(slotsData);
        // When deleting a category/item, keep the same slot expanded; otherwise close all
        if (keepExpandedSlotID != null && keepExpandedSlotID > 0) {
          const indexToExpand = slotsData.findIndex(s => (s.slotID || s.SlotID) === keepExpandedSlotID);
          if (indexToExpand >= 0) {
            setExpandedSlots(new Set([indexToExpand]));
          } else {
            setExpandedSlots(new Set());
          }
        } else {
          setExpandedSlots(new Set());
        }
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      showErrorToast("Failed to refresh slot data");
    }
  }, []);

  useEffect(() => {
    if (isChildBranch) return;
    if (dealId && CompID) {
      fetchDealData();
    }
  }, [dealId, CompID, fetchDealData, isChildBranch]);

  const handleInputChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    // When deal pricing type is changed to NOT SlotPricing, every slot defaults to NoDiscount
    if (field === "dealPricingType" && (value || "").trim() !== "SlotPricing") {
      setSlots((prev) =>
        prev.map((slot) => ({
          ...slot,
          pricingType: "NoDiscount",
          discountPercentage: null,
          isFree: false,
          customPrice: null,
          _isUnsaved: true,
        }))
      );
    }
  };

  const handleToggleChange = (field, value) => {
    setToggles((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (imageUrl, imageFile) => {
    setImageState({
      dealImage: imageUrl,
      imageFile: imageFile,
    });
  };

  const handleSaveDeal = async (options = {}) => {
    const { exitAfterSave = false } = options;
    try {
      setSaving(true);

      // Auto-save only unsaved slots that have data
      const currentSlots = slots;
      if (Array.isArray(currentSlots) && currentSlots.length > 0) {
        for (let i = 0; i < currentSlots.length; i++) {
          const slot = currentSlots[i];
          const hasName = slot && slot.slotName && String(slot.slotName).trim();
          const isUnsavedSlot = slot && (slot.slotID === 0 || slot._isUnsaved === true);
          if (hasName && isUnsavedSlot) {
            try {
              await saveSlot(i);
            } catch (slotErr) {
              console.error("Auto-save slot failed:", slotErr);
              // Continue with other slots; deal save will still run
            }
          }
        }
      }

      const currentFormState = formStateRef.current;
      if (currentFormState.dealPricingType === "CustomPrice") {
        const priceVal = currentFormState.dealPrice;
        if (priceVal === undefined || priceVal === null || priceVal.toString().trim() === "") {
          showErrorToast("Deal price is required for Fixed Price");
          setSaving(false);
          return;
        }
        if (parseFloat(priceVal) < 0) {
          showErrorToast("Deal price cannot be negative");
          setSaving(false);
          return;
        }
      }

      // Upload image if there's a new file
      let imageUrl = imageState.dealImage;
      const baseURL = axiosInstance.defaults.baseURL || "";
      try {
        if (imageState.imageFile) {
          const imageFormData = new FormData();
          imageFormData.append("ImageFile", imageState.imageFile);
          const imageResponse = await axiosInstance.post(
            APIs.POST_UPLOAD_PRODUCT_IMAGE,
            imageFormData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          const relativePath = imageResponse.data?.data?.filePath;
          if (relativePath) {
            // Store relative path in database (backend will add baseURL when fetching)
            imageUrl = relativePath;
          }
        } else if (imageState.dealImage && !imageState.dealImage.startsWith("http")) {
          // If image exists but is not a full URL, ensure it's a relative path
          // Remove base URL if it was accidentally included
          const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
          imageUrl = imageState.dealImage.replace(baseUrlClean, "").replace(/^\/+/, "");
        } else if (imageState.dealImage && imageState.dealImage.startsWith("http")) {
          // If it's a full URL, extract relative path for storage
          const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
          imageUrl = imageState.dealImage.replace(baseUrlClean, "").replace(/^\/+/, "");
        }
      } catch (err) {
        showErrorToast("Failed to upload image");
      }

      // Format dates properly for ASP.NET Core - use ISO format with T separator
      let formattedStartDate = null;
      let formattedEndDate = null;
      
      if (currentFormState.startDate) {
        // Handle both Date objects and moment objects
        const startDate = currentFormState.startDate instanceof Date 
          ? currentFormState.startDate 
          : currentFormState.startDate instanceof moment 
            ? currentFormState.startDate.toDate() 
            : new Date(currentFormState.startDate);
        
        const startMoment = moment(startDate);
        if (startMoment.isValid()) {
          // Format as ISO 8601: YYYY-MM-DDTHH:mm:ss (ASP.NET Core expects this format)
          formattedStartDate = startMoment.format("YYYY-MM-DDTHH:mm:ss");
        }
      }
      
      if (currentFormState.endDate) {
        // Handle both Date objects and moment objects
        const endDate = currentFormState.endDate instanceof Date 
          ? currentFormState.endDate 
          : currentFormState.endDate instanceof moment 
            ? currentFormState.endDate.toDate() 
            : new Date(currentFormState.endDate);
        
        const endMoment = moment(endDate);
        if (endMoment.isValid()) {
          formattedEndDate = endMoment.format("YYYY-MM-DDTHH:mm:ss");
        }
      }

      // Format weekly timings for the deal payload
      let dealWeeklyTimings = null;
      if (toggles.isScheduleDeal) {
        const formattedSchedules = [];
        if (useSameTimeSlots) {
          const days = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];
          days.forEach((day) => {
            if (commonTimeSlot.length > 0) {
              commonTimeSlot.forEach((slot) => {
                if (
                  moment(slot.from, ["h:mm A"]).isValid() &&
                  moment(slot.to, ["h:mm A"]).isValid()
                ) {
                  formattedSchedules.push({
                    dealID: currentFormState.dealID,
                    compID: CompID,
                    dayName: day,
                    openTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                    closeTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                    updateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        } else {
          schedules.forEach((schedule) => {
            if (schedule.checked && schedule.timeSlots.length > 0) {
              schedule.timeSlots.forEach((slot) => {
                if (
                  moment(slot.from, ["h:mm A"]).isValid() &&
                  moment(slot.to, ["h:mm A"]).isValid()
                ) {
                  formattedSchedules.push({
                    dealID: currentFormState.dealID,
                    compID: CompID,
                    dayName: schedule.day,
                    openTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                    closeTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                    updateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        }
        
        if (formattedSchedules.length > 0) {
          dealWeeklyTimings = formattedSchedules;
        }
      }

      const pricingType = (currentFormState.dealPricingType || "").trim();
      const dealData = {
        dealID: currentFormState.dealID,
        compID: CompID,   
        dealName: currentFormState.dealName.trim(),
        dealDescription: currentFormState.dealDescription.trim() || null,
        dealImage: imageUrl || null,
        dealPrice: pricingType === "CustomPrice" && currentFormState.dealPrice ? parseFloat(currentFormState.dealPrice) : null,
        isActive: toggles.isActive,
        showInKiosk: toggles.showInKiosk,
        showInPOS: toggles.showInPOS,
        showInOnline: toggles.showInOnline,
        dealOrder: parseInt(currentFormState.dealOrder) || 0,
        pricingType: currentFormState.dealPricingType || null,
        discountPercentage: pricingType === "Discount" && currentFormState.dealDiscountPercentage ? parseFloat(currentFormState.dealDiscountPercentage) : null,
        autoDuplicateItem: toggles.autoDuplicateItem || false,
        minimumOrderValue: currentFormState.minimumOrderValue ? parseFloat(currentFormState.minimumOrderValue) : null,
        isScheduleDeal: toggles.isScheduleDeal || false,
        dealWeeklyTimings: dealWeeklyTimings,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        modifiedBy: modifiedBy,
      };

      const response = await axiosInstance.post(APIs.POST_ADD_OR_UPDATE_DEAL, dealData);

      if (response.data.status) {
        showSuccessToast("Deal updated successfully!");
        if (exitAfterSave) {
          navigate("/deal/manageDeals");
        }
      } else {
        showErrorToast(response.data.message || "Failed to update deal");
      }
    } catch (error) {
      console.error("Error updating deal:", error);
      showErrorToast("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const addSlotRow = () => {
    const newSlot = {
      slotID: 0, // 0 means new slot
      dealID: formState.dealID,
      slotName: "",
      requiredQty: 0,
      slotOrder: slots.length,
      allowedCategories: [],
      allowedItems: [],
      constraint: null,
      buyQuantity: 0,
      pricingType: formState.dealPricingType === "SlotPricing" ? "NoDiscount" : null,
      discountPercentage: null,
      isFree: false,
      customPrice: null,
      autoDuplicateItem: false,
      _isUnsaved: true,
    };
    setSlots((prev) => {
      const newSlots = [...prev, newSlot];
      // Expand the new slot automatically
      setExpandedSlots((prevExpanded) => {
        const newExpanded = new Set(prevExpanded);
        newExpanded.add(newSlots.length - 1);
        return newExpanded;
      });
      return newSlots;
    });
  };

  const handleSlotChange = (index, field, value) => {
    setSlots((prev) => {
      const updatedSlots = [...prev];
      updatedSlots[index] = { ...updatedSlots[index], [field]: value, _isUnsaved: true };
      return updatedSlots;
    });
  };

  const handleConstraintChange = (index, field, value) => {
    const updatedSlots = [...slots];
    if (!updatedSlots[index].constraint) {
      updatedSlots[index].constraint = {
        minQty: null,
        maxQty: null,
        allowDuplicateItems: true,
        allowMixedCategories: true,
      };
    }
    updatedSlots[index].constraint = {
      ...updatedSlots[index].constraint,
      [field]: value,
    };
    updatedSlots[index]._isUnsaved = true;
    setSlots(updatedSlots);
  };

  const saveSlot = async (index) => {
    const slot = slots[index];
    
    if (!slot.slotName || !slot.slotName.trim()) {
      showErrorToast("Slot name is required");
      return;
    }

    try {
      setSlotSaving(true);
      const slotData = {
        slotID: slot.slotID || 0,
        dealID: formState.dealID,
        slotName: slot.slotName.trim(),
        requiredQty: parseInt(slot.requiredQty) || 0,
        slotOrder: parseInt(slot.slotOrder) || 0,
        selectedVariant: slot.selectedVariant || null,
        buyQuantity: slot.buyQuantity !== undefined && slot.buyQuantity !== null ? (parseInt(slot.buyQuantity) || 0) : 0,
        pricingType: formState.dealPricingType === "SlotPricing" ? (slot.pricingType || "NoDiscount") : null,
        discountPercentage: slot.discountPercentage ?? null,
        isFree: slot.isFree || false,
        customPrice: slot.customPrice != null && slot.customPrice !== "" ? (isNaN(parseFloat(slot.customPrice)) ? null : parseFloat(slot.customPrice)) : null,
        autoDuplicateItem: slot.autoDuplicateItem ?? false,
      };

      const response = await axiosInstance.post(APIs.POST_ADD_OR_UPDATE_DEAL_SLOT, slotData);

      if (response.data.status) {
        const savedSlot = response.data.data;
        
        // Save constraints if they exist
        if (slot.constraint) {
          await axiosInstance.post(APIs.POST_ADD_OR_UPDATE_DEAL_SLOT_CONSTRAINT, {
            slotID: savedSlot.slotID,
            minQty: slot.constraint.minQty ? parseInt(slot.constraint.minQty) : null,
            maxQty: slot.constraint.maxQty ? parseInt(slot.constraint.maxQty) : null,
            allowDuplicateItems: slot.constraint.allowDuplicateItems !== false,
            allowMixedCategories: slot.constraint.allowMixedCategories !== false,
          });
        }
        
        // Save categories if they exist (for new slots)
        if (slot.allowedCategories && slot.allowedCategories.length > 0) {
          const categoryPromises = slot.allowedCategories.map(cat => {
            const categoryID = cat.categoryID || cat.CategoryID;
            if (!categoryID) return null;
            return axiosInstance.post(APIs.POST_ADD_DEAL_SLOT_ALLOWED_CATEGORY, {
              slotID: savedSlot.slotID,
              categoryID: categoryID,
            });
          });
          await Promise.all(categoryPromises.filter(p => p !== null));
        }
        
        // Save items if they exist (for new slots)
        if (slot.allowedItems && slot.allowedItems.length > 0) {
          const itemPromises = slot.allowedItems.map(item => {
            const itemID = item.itemID || item.ItemID;
            if (!itemID) return null;
            return axiosInstance.post(APIs.POST_ADD_DEAL_SLOT_ALLOWED_ITEM, {
              slotID: savedSlot.slotID,
              itemID: itemID,
            });
          });
          await Promise.all(itemPromises.filter(p => p !== null));
        }
        
        showSuccessToast(slot.slotID === 0 ? "Slot created successfully!" : "Slot updated successfully!");
        // Close the slot card after successful save
        setExpandedSlots((prev) => {
          const newExpanded = new Set(prev);
          newExpanded.delete(index);
          return newExpanded;
        });
        // Refresh slots to get updated data
        await fetchSlots(formState.dealID, null, formStateRef.current.dealPricingType);
      } else {
        showErrorToast(response.data.message || "Failed to save slot");
      }
      setSlotSaving(false);
    } catch (error) {
      console.error("Error saving slot:", error);
      showErrorToast("Error saving slot");
      setSlotSaving(false);
    }
  };

  const removeSlotRow = async (slot, index) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete slot "${slot.slotName || "this slot"}"? All associated categories, items, and constraints will also be deleted.`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      if (slot.slotID > 0) {
        // Existing slot - delete from database
        setSlotSaving(true);
        const response = await axiosInstance.delete(APIs.DELETE_DEAL_SLOT, {
          params: { SlotID: slot.slotID },
        });

        if (response.data.status) {
          showSuccessToast("Slot deleted successfully!");
          setSlots((prev) => prev.filter((_, i) => i !== index));
          // Remove from expanded set
          setExpandedSlots((prev) => {
            const newExpanded = new Set(prev);
            newExpanded.delete(index);
            return newExpanded;
          });
        } else {
          showErrorToast(response.data.message || "Deletion failed");
        }
        setSlotSaving(false);
      } else {
        // New slot - just remove from local state
        setSlots((prev) => prev.filter((_, i) => i !== index));
        // Remove from expanded set
        setExpandedSlots((prev) => {
          const newExpanded = new Set(prev);
          newExpanded.delete(index);
          return newExpanded;
        });
      }
    } catch (err) {
      showErrorToast("Failed to delete slot");
      setSlotSaving(false);
    }
  };

  const handleManageCategories = useCallback((slot, index) => {
    if (!slot) {
      console.error("handleManageCategories: slot is null or undefined");
      return;
    }
    
    // Find slot index if not provided
    const slotIndex = index !== undefined ? index : slots.findIndex(s => 
      s && (s.slotID === slot.slotID && s.slotID !== 0 ? true : 
      (s.slotName === slot.slotName && s.requiredQty === slot.requiredQty && s.slotOrder === slot.slotOrder))
    );
    
    selectedSlotRef.current = slot; // Store in ref FIRST
    selectedSlotIndexRef.current = slotIndex; // Store index
    setSelectedSlot(slot);
    
    // Check if slot already has categories or items
    const hasCategories = slot.allowedCategories && slot.allowedCategories.length > 0;
    const hasItems = slot.allowedItems && slot.allowedItems.length > 0;
    
    // If slot has neither, show type selection modal
    if (!hasCategories && !hasItems) {
      setShowTypeSelectionModal(true);
      return;
    }
    
    // If slot already has categories, directly show category modal
    if (hasCategories) {
      // Ensure slot is preserved in ref (already set above, but ensure it's current)
      selectedSlotRef.current = slot;
      selectedSlotIndexRef.current = slotIndex;
      setSelectedSlot(slot);
      const categories = slot.allowedCategories.map(cat => {
        const catId = parseInt(cat.categoryID ?? cat.CategoryID, 10);
        const flat = flattenCategories(allCategories);
        const category = flat.find(
          (c) => parseInt(c.rootCat_ID ?? c.RootCat_ID ?? c.id ?? c.ID, 10) === catId
        );
        const name =
          cat.categoryName ||
          cat.CategoryName ||
          cat.rootCat_Name ||
          cat.RootCat_Name ||
          category?.rootCat_Name ||
          category?.RootCat_Name ||
          getCategoryName(catId);
        return (
          category || {
            rootCat_ID: catId,
            rootCat_Name: name,
          }
        );
      });
      setSelectedCategories(categories);
      setSelectedType('categories');
      setShowCategoryModal(true);
      return;
    }
    
    // If slot has items but user wants to switch to categories, show confirmation
    if (hasItems) {
      swal({
        title: "Switch to Categories?",
        text: "This slot currently has items selected. Switching to categories will remove all selected items. Do you want to continue?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then((confirmed) => {
        if (confirmed) {
          // Ensure slot is preserved in ref before opening modal
          selectedSlotRef.current = slot;
          selectedSlotIndexRef.current = slotIndex;
          setSelectedSlot(slot);
          // Clear items first
          setSelectedCategories([]);
          setSelectedType('categories');
          setShowCategoryModal(true);
        }
      });
      return;
    }
  }, [allCategories, slots]);

  const handleSaveCategories = useCallback(async (selectedCats) => {
    // Use ref to get current slot value (preserved even if state changes)
    let currentSlot = selectedSlotRef.current || selectedSlot;
    
    // Fallback: if slot is null, try to find it from slots array using the index ref
    if (!currentSlot && selectedSlotIndexRef.current !== null && selectedSlotIndexRef.current >= 0) {
      currentSlot = slots[selectedSlotIndexRef.current];
      if (currentSlot) {
        // Restore the refs
        selectedSlotRef.current = currentSlot;
        setSelectedSlot(currentSlot);
      }
    }
    
    if (!currentSlot) {
      console.error("No selected slot for saving categories. selectedSlot:", selectedSlot, "ref:", selectedSlotRef.current, "indexRef:", selectedSlotIndexRef.current);
      showErrorToast("No slot selected. Please try again.");
      return;
    }
    
    const slotIndex = selectedSlotIndexRef.current !== null ? selectedSlotIndexRef.current : 
      slots.findIndex(s => 
        s && (currentSlot.slotID !== 0 
          ? s.slotID === currentSlot.slotID 
          : (s.slotName === currentSlot.slotName && s.requiredQty === currentSlot.requiredQty && s.slotOrder === currentSlot.slotOrder))
      );

    if (slotIndex === -1) {
      console.error("Slot not found in slots array. slot:", currentSlot, "slots:", slots);
      showErrorToast("Slot not found. Please try again.");
      return;
    }

    try {
      const slotIDToRefresh = currentSlot.slotID;
      
      // Use selectedCats from CategorySelector callback (it's passed directly)
      const categoriesToSave = selectedCats || selectedCategories;
      console.log("Saving categories:", categoriesToSave, "for slot:", currentSlot.slotID);
      
      if (!categoriesToSave || !Array.isArray(categoriesToSave)) {
        console.error("Invalid categories data:", categoriesToSave);
        showErrorToast("Invalid category data");
        return;
      }
      
      // Update local state IMMEDIATELY (optimistic update) - like updateItem.jsx
      if (slotIndex !== -1) {
        const normalizedCategories = categoriesToSave.map(cat => {
          const categoryID = cat.rootCat_ID || cat.RootCat_ID || cat.categoryID || cat.CategoryID;
          const categoryName =
            cat.rootCat_Name ||
            cat.RootCat_Name ||
            cat.categoryName ||
            cat.CategoryName ||
            getCategoryName(categoryID);
          return {
            id: null,
            slotID: currentSlot.slotID,
            categoryID,
            categoryName,
            rootCat_Name: categoryName,
          };
        });
        
        setSlots(prev => prev.map((slot, idx) => 
          idx === slotIndex 
            ? {
                ...slot,
                allowedCategories: normalizedCategories,
                allowedItems: [], // Clear items (mutually exclusive)
                _isUnsaved: true,
              }
            : slot
        ));
      }
      
      // For new slots (slotID: 0), only update local state - don't save to backend yet
      // User needs to save the slot first, then categories will be saved
      if (currentSlot.slotID === 0) {
        // Close modals after updating local state
        setShowCategoryModal(false);
        setShowTypeSelectionModal(false);
        setSelectedSlot(null);
        setSelectedType(null);
        selectedSlotRef.current = null;
        selectedSlotIndexRef.current = null;
        typeSelectionInProgressRef.current = false;
        showSuccessToast("Categories selected! Please save the slot to persist changes.");
        return;
      }
      
      // For existing slots, save to backend
      // Delete existing categories AND items (mutually exclusive) from backend
      await Promise.all([
        axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_CATEGORIES_BY_SLOT_ID, {
          params: { SlotID: currentSlot.slotID },
        }),
        axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_ITEMS_BY_SLOT_ID, {
          params: { SlotID: currentSlot.slotID },
        }),
      ]);

      // Add new categories to backend
      if (categoriesToSave.length > 0) {
        const savePromises = categoriesToSave.map(category => {
          const categoryID =
            category.rootCat_ID ||
            category.RootCat_ID ||
            category.categoryID ||
            category.CategoryID;
          if (!categoryID) {
            console.error("Category missing ID:", category);
            return null;
          }
          const payload = {
            slotID: currentSlot.slotID,
            categoryID: categoryID,
          };
          console.log("Saving category payload:", payload);
          return axiosInstance.post(APIs.POST_ADD_DEAL_SLOT_ALLOWED_CATEGORY, payload);
        });
        
        // Filter out null promises
        const validPromises = savePromises.filter(p => p !== null);
        if (validPromises.length > 0) {
          await Promise.all(validPromises);
        }
      }

      showSuccessToast("Categories updated successfully!");
      
      // Refresh slots data from backend to ensure sync (keep this slot expanded)
      await fetchSlots(formState.dealID, slotIDToRefresh, formStateRef.current.dealPricingType);
      
      // Close all modals after save completes
      setShowCategoryModal(false);
      setShowTypeSelectionModal(false);
      setSelectedSlot(null);
      setSelectedType(null);
      selectedSlotRef.current = null;
      selectedSlotIndexRef.current = null;
      typeSelectionInProgressRef.current = false;
    } catch (error) {
      console.error("Error saving categories:", error);
      showErrorToast("Failed to save categories: " + (error.response?.data?.message || error.message));
      // Revert on error by refreshing from backend (keep this slot expanded)
      await fetchSlots(formState.dealID, currentSlot?.slotID ?? null, formStateRef.current.dealPricingType);
      // Close modals even on error
      setShowCategoryModal(false);
      setShowTypeSelectionModal(false);
      setSelectedSlot(null);
      setSelectedType(null);
      selectedSlotRef.current = null;
      selectedSlotIndexRef.current = null;
      typeSelectionInProgressRef.current = false;
    }
  }, [selectedSlot, selectedCategories, formState.dealID, fetchSlots, slots, getCategoryName]);

  const handleManageItems = useCallback((slot, index) => {
    if (!slot) {
      console.error("handleManageItems: slot is null or undefined");
      return;
    }
    
    // Find slot index if not provided
    const slotIndex = index !== undefined ? index : slots.findIndex(s => 
      s && (s.slotID === slot.slotID && s.slotID !== 0 ? true : 
      (s.slotName === slot.slotName && s.requiredQty === slot.requiredQty && s.slotOrder === slot.slotOrder))
    );
    
    selectedSlotRef.current = slot; // Store in ref FIRST
    selectedSlotIndexRef.current = slotIndex; // Store index
    setSelectedSlot(slot);
    
    // Check if slot already has categories or items
    const hasCategories = slot.allowedCategories && slot.allowedCategories.length > 0;
    const hasItems = slot.allowedItems && slot.allowedItems.length > 0;
    
    // If slot has neither, show type selection modal
    if (!hasCategories && !hasItems) {
      setShowTypeSelectionModal(true);
      return;
    }
    
    // If slot already has items, directly show item modal
    if (hasItems) {
      // Ensure slot is preserved in ref (already set above, but ensure it's current)
      selectedSlotRef.current = slot;
      selectedSlotIndexRef.current = slotIndex;
      setSelectedSlot(slot);
      // ProductMultiSelectionModal expects IDs; it can also accept objects but normalizes to IDs anyway.
      // We'll pass IDs to keep the shape consistent.
      const itemIds = slot.allowedItems
        .map((it) => {
          const raw =
            typeof it === "object"
              ? (it.itemID || it.ItemID || it.menuItem_ID || it.MenuItem_ID || it.id)
              : it;
          const id = raw !== undefined && raw !== null ? parseInt(raw, 10) : null;
          return id || null;
        })
        .filter(Boolean);
      setSelectedItems(itemIds);
      setSelectedType('items');
      setShowItemModal(true);
      return;
    }
    
    // If slot has categories but user wants to switch to items, show confirmation
    if (hasCategories) {
      swal({
        title: "Switch to Items?",
        text: "This slot currently has categories selected. Switching to items will remove all selected categories. Do you want to continue?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then((confirmed) => {
        if (confirmed) {
          // Ensure slot is preserved in ref before opening modal
          selectedSlotRef.current = slot;
          selectedSlotIndexRef.current = slotIndex;
          setSelectedSlot(slot);
          // Clear categories first
          setSelectedItems([]);
          setSelectedType('items');
          setShowItemModal(true);
        }
      });
      return;
    }
  }, [allItems, slots]);
  
  const handleTypeSelection = useCallback((type) => {
    const currentSlot = selectedSlotRef.current || selectedSlot;
    if (!currentSlot) {
      console.error("No slot selected for type selection");
      return;
    }
    
    // Mark that type selection is in progress - prevents onClose from clearing slot
    typeSelectionInProgressRef.current = true;
    
    // Ensure slot is preserved in ref before opening modal
    // This prevents it from being cleared by onClose callback
    selectedSlotRef.current = currentSlot;
    setSelectedSlot(currentSlot);
    
    setSelectedType(type);
    
    if (type === 'categories') {
      // Convert allowed categories to the format expected by CategorySelector
      const categories = currentSlot.allowedCategories?.map(cat => {
        const catId = parseInt(cat.categoryID ?? cat.CategoryID, 10);
        const flat = flattenCategories(allCategories);
        const category = flat.find(
          (c) => parseInt(c.rootCat_ID ?? c.RootCat_ID ?? c.id ?? c.ID, 10) === catId
        );
        const name =
          cat.categoryName ||
          cat.CategoryName ||
          cat.rootCat_Name ||
          cat.RootCat_Name ||
          category?.rootCat_Name ||
          category?.RootCat_Name ||
          getCategoryName(catId);
        return (
          category || {
            rootCat_ID: catId,
            rootCat_Name: name,
          }
        );
      }) || [];
      setSelectedCategories(categories);
      // Open modal immediately - onClose will check ref to know we're transitioning
      setShowCategoryModal(true);
    } else if (type === 'items') {
      // Convert allowed items to the format expected by ProductMultiSelectionModal
      const items = currentSlot.allowedItems?.map(item => {
        const itemID = item.itemID || item.ItemID;
        const menuItem = allItems.find(i => i.menuItem_ID === itemID);
        return menuItem || { menuItem_ID: itemID, menuItem_Name: `Item ${itemID}` };
      }) || [];
      setSelectedItems(items);
      // Open modal immediately - onClose will check ref to know we're transitioning
      setShowItemModal(true);
    }
    
    // Reset flag after a short delay to allow modal to open
    setTimeout(() => {
      typeSelectionInProgressRef.current = false;
    }, 200);
  }, [selectedSlot, allCategories, allItems]);

  const handleSaveItems = useCallback(async (selectedItemsFromModal) => {
    // Use ref to get current slot value (preserved even if state changes)
    let currentSlot = selectedSlotRef.current || selectedSlot;
    
    // Fallback: if slot is null, try to find it from slots array using the index ref
    if (!currentSlot && selectedSlotIndexRef.current !== null && selectedSlotIndexRef.current >= 0) {
      currentSlot = slots[selectedSlotIndexRef.current];
      if (currentSlot) {
        // Restore the refs
        selectedSlotRef.current = currentSlot;
        setSelectedSlot(currentSlot);
      }
    }
    
    if (!currentSlot) {
      console.error("No selected slot for saving items. selectedSlot:", selectedSlot, "ref:", selectedSlotRef.current, "indexRef:", selectedSlotIndexRef.current);
      showErrorToast("No slot selected. Please try again.");
      return;
    }
    
    const slotIndex = selectedSlotIndexRef.current !== null ? selectedSlotIndexRef.current : 
      slots.findIndex(s => 
        s && (currentSlot.slotID !== 0 
          ? s.slotID === currentSlot.slotID 
          : (s.slotName === currentSlot.slotName && s.requiredQty === currentSlot.requiredQty && s.slotOrder === currentSlot.slotOrder))
      );

    if (slotIndex === -1) {
      console.error("Slot not found in slots array. slot:", currentSlot, "slots:", slots);
      showErrorToast("Slot not found. Please try again.");
      return;
    }

    try {
      const slotIDToRefresh = currentSlot.slotID;
      
      // ProductMultiSelectionModal returns an array of IDs (numbers/strings), not objects
      const itemsToSave = selectedItemsFromModal || selectedItems;
      console.log("Saving items:", itemsToSave, "for slot:", currentSlot.slotID);
      
      if (!itemsToSave || !Array.isArray(itemsToSave)) {
        console.error("Invalid items data:", itemsToSave);
        showErrorToast("Invalid item data");
        return;
      }
      
      // Update local state IMMEDIATELY (optimistic update) - like updateItem.jsx
      if (slotIndex !== -1) {
        const normalizedItems = itemsToSave
          .map((item) => {
            const itemIDRaw =
              typeof item === "object"
                ? (item.menuItem_ID || item.MenuItem_ID || item.itemID || item.ItemID || item.id)
                : item;
            const itemID = itemIDRaw !== undefined && itemIDRaw !== null ? parseInt(itemIDRaw, 10) : null;
            return itemID
              ? {
                  id: null,
                  slotID: currentSlot.slotID,
                  itemID: itemID,
                }
              : null;
          })
          .filter(Boolean);
        
        setSlots(prev => prev.map((slot, idx) => 
          idx === slotIndex 
            ? {
                ...slot,
                allowedItems: normalizedItems,
                allowedCategories: [], // Clear categories (mutually exclusive)
                _isUnsaved: true,
              }
            : slot
        ));
      }
      
      // For new slots (slotID: 0), only update local state - don't save to backend yet
      // User needs to save the slot first, then items will be saved
      if (currentSlot.slotID === 0) {
        // Close modals after updating local state
        setShowItemModal(false);
        setShowTypeSelectionModal(false);
        setSelectedSlot(null);
        setSelectedType(null);
        selectedSlotRef.current = null;
        selectedSlotIndexRef.current = null;
        typeSelectionInProgressRef.current = false;
        showSuccessToast("Items selected! Please save the slot to persist changes.");
        return;
      }
      
      // For existing slots, save to backend
      // Delete existing items AND categories (mutually exclusive) from backend
      await Promise.all([
        axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_ITEMS_BY_SLOT_ID, {
          params: { SlotID: currentSlot.slotID },
        }),
        axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_CATEGORIES_BY_SLOT_ID, {
          params: { SlotID: currentSlot.slotID },
        }),
      ]);

      // Add new items to backend
      if (itemsToSave.length > 0) {
        const savePromises = itemsToSave.map(item => {
          const itemIDRaw =
            typeof item === "object"
              ? (item.menuItem_ID || item.MenuItem_ID || item.itemID || item.ItemID || item.id)
              : item;
          const itemID = itemIDRaw !== undefined && itemIDRaw !== null ? parseInt(itemIDRaw, 10) : null;
          if (!itemID) {
            console.error("Item missing ID:", item);
            return null;
          }
          const payload = {
            slotID: currentSlot.slotID,
            itemID: itemID,
          };
          console.log("Saving item payload:", payload);
          return axiosInstance.post(APIs.POST_ADD_DEAL_SLOT_ALLOWED_ITEM, payload);
        });
        
        // Filter out null promises
        const validPromises = savePromises.filter(p => p !== null);
        if (validPromises.length > 0) {
          await Promise.all(validPromises);
        }
      }

      showSuccessToast("Items updated successfully!");
      
      // Refresh slots data from backend to ensure sync (keep this slot expanded)
      await fetchSlots(formState.dealID, currentSlot.slotID, formStateRef.current.dealPricingType);
      
      // Close all modals after save completes
      setShowItemModal(false);
      setShowTypeSelectionModal(false);
      setSelectedSlot(null);
      setSelectedType(null);
      selectedSlotRef.current = null;
      selectedSlotIndexRef.current = null;
      typeSelectionInProgressRef.current = false;
    } catch (error) {
      console.error("Error saving items:", error);
      showErrorToast("Failed to save items: " + (error.response?.data?.message || error.message));
      // Revert on error by refreshing from backend (keep this slot expanded)
      await fetchSlots(formState.dealID, currentSlot?.slotID ?? null, formStateRef.current.dealPricingType);
      // Close modals even on error
      setShowItemModal(false);
      setShowTypeSelectionModal(false);
      setSelectedSlot(null);
      setSelectedType(null);
      selectedSlotRef.current = null;
      selectedSlotIndexRef.current = null;
      typeSelectionInProgressRef.current = false;
    }
  }, [selectedSlot, selectedItems, formState.dealID, fetchSlots, slots]);



  const handleDeleteCategory = async (categoryID, slotID) => {
    try {
      const response = await axiosInstance.get(APIs.GET_DEAL_SLOT_ALLOWED_CATEGORIES, {
        params: { SlotID: slotID },
      });

      if (response.data.status && response.data.data) {
        // Handle both camelCase and PascalCase from API
        const category = response.data.data.find(c => 
          (c.categoryID === categoryID) || (c.CategoryID === categoryID)
        );
        if (category) {
          const categoryDbID = category.id || category.ID;
          await axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_CATEGORY, {
            params: { ID: categoryDbID },
          });
          showSuccessToast("Category removed successfully!");
          await fetchSlots(formState.dealID, slotID, formStateRef.current.dealPricingType);
        }
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      showErrorToast("Failed to remove category");
    }
  };

  const handleDeleteItem = async (itemID, slotID) => {
    try {
      const response = await axiosInstance.get(APIs.GET_DEAL_SLOT_ALLOWED_ITEMS, {
        params: { SlotID: slotID },
      });

      if (response.data.status && response.data.data) {
        // Handle both camelCase and PascalCase from API
        const item = response.data.data.find(i => 
          (i.itemID === itemID) || (i.ItemID === itemID)
        );
        if (item) {
          const itemDbID = item.id || item.ID;
          await axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_ITEM, {
            params: { ID: itemDbID },
          });
          showSuccessToast("Item removed successfully!");
          await fetchSlots(formState.dealID, slotID, formStateRef.current.dealPricingType);
        }
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      showErrorToast("Failed to remove item");
    }
  };

  const getItemName = (itemID) => {
    const id = parseInt(itemID, 10);
    const item = allItems.find(i => parseInt(i.menuItem_ID || i.MenuItem_ID || i.id, 10) === id);
    return item ? (item.menuItem_Name || item.MenuItem_Name || item.name) : `Item ${itemID}`;
  };

  if (loading) {
    return (
      <div className="p-3">
        <GridSectionLoader loading={true} minHeight="70vh" />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', minHeight: '100vh', backgroundColor: isDarkMode ? '#1a1d29' : '#f5f7fa' }}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <ol className="breadcrumb mb-2" style={{ backgroundColor: 'transparent', padding: 0 }}>
            <li className="breadcrumb-item">
              <a href="#/" style={{ color: isDarkMode ? '#a0aec0' : '#6c757d', textDecoration: 'none' }}>Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/deal/manageDeals" style={{ color: isDarkMode ? '#a0aec0' : '#6c757d', textDecoration: 'none' }}>Manage Deals</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: themeColor }}>Update Deal</li>
          </ol>
          <h1 className="mb-0" style={{ 
            fontSize: '28px', 
            fontWeight: '700',
            color: isDarkMode ? '#ffffff' : '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <i className="fa fa-tags" style={{ color: themeColor }}></i>
            Update Deal
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <button
            type="button"
            className="btn"
            onClick={() => handleSaveDeal({ exitAfterSave: false })}
            disabled={saving}
            style={{
              background: isDarkMode ? '#495057' : '#6c757d',
              border: 'none',
              color: 'white',
              borderRadius: '10px',
              padding: '10px 24px',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              height: '44px'
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
              }
            }}
            onMouseLeave={(e) => {
              if (!saving) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }
            }}
          >
            <i className="fa fa-save"></i>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => handleSaveDeal({ exitAfterSave: true })}
            disabled={saving}
            style={{
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '10px',
              padding: '10px 24px',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 4px 12px ${themeColor}40`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              height: '44px'
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 6px 16px ${themeColor}60`;
              }
            }}
            onMouseLeave={(e) => {
              if (!saving) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}40`;
              }
            }}
          >
            <i className="fa fa-save"></i>
            {saving ? 'Saving...' : 'Save and Exit'}
          </button>
        </div>
      </div>

      <GridSectionLoader loading={saving} minHeight="400px">
      <Panel className="m-3">
        <PanelHeader>General</PanelHeader>
        <PanelBody>
          <div 
            className="p-4 rounded-3 shadow-sm"
            style={{
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              border: isDarkMode ? '1px solid #404040' : undefined,
              color: isDarkMode ? '#ffffff' : '#000000',
            }}
          >
            <div className="row mb-4 align-items-center">
              <div className="col-md-3 text-center">
                <ImageUpload
                  imageUrl={imageState.dealImage}
                  imageFile={imageState.imageFile}
                  setImage={(url, file) =>
                    setImageState({ dealImage: url, imageFile: file })
                  }
                />
              </div>
              <div className="col-md-9">
                <div className="row row-cols-2 g-3">
                  <div className="col">
                    <ToggleSwitch
                      label="Is Active"
                      isChecked={toggles.isActive}
                      onToggle={(checked) => handleToggleChange("isActive", checked)}
                      themeColor={themeColor}
                    />
                  </div>
                  <div className="col">
                    <ToggleSwitch
                      label="Show in Kiosk"
                      isChecked={toggles.showInKiosk}
                      onToggle={(checked) => handleToggleChange("showInKiosk", checked)}
                      themeColor={themeColor}
                    />
                  </div>
                  <div className="col">
                    <ToggleSwitch
                      label="Show in POS"
                      isChecked={toggles.showInPOS}
                      onToggle={(checked) => handleToggleChange("showInPOS", checked)}
                      themeColor={themeColor}
                    />
                  </div>
                  <div className="col">
                    <ToggleSwitch
                      label="Show in Online"
                      isChecked={toggles.showInOnline}
                      onToggle={(checked) => handleToggleChange("showInOnline", checked)}
                      themeColor={themeColor}
                    />
                  </div>
                </div>
              </div>
            </div>
          <div className="row g-4">
            <div className="col-md-6">
              <FormInput
                label="Deal Name *"
                type="text"
                value={formState.dealName}
                onchange={(e) => handleInputChange("dealName", e.target.value)}
                placeHolder="Enter deal name"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold mb-2">Deal Pricing Type</label>
              <Select
                options={[
                  { value: "CustomPrice", label: "Fixed Price" },
                  { value: "Discount", label: "Custom Discount %" },
                  { value: "SlotPricing", label: "Slot Pricing" }
                ]}
                value={(() => {
                  const pt = formState.dealPricingType ?? "CustomPrice";
                  const labels = {
                    Fixed: "Used Deal Pricing",
                    Free: "Free",
                    HalfPrice: "Half Price (50% Off)",
                    Discount: "Custom Discount %",
                    CustomPrice: "Fixed Price",
                    SlotPricing: "Slot Pricing"
                  };
                  const label = labels[pt] || pt || "Fixed Price";
                  return { value: pt, label };
                })()}
                onChange={(option) => {
                  const newPricingType = option ? option.value : "CustomPrice";
                  setFormState(prev => ({
                    ...prev,
                    dealPricingType: newPricingType,
                    dealPrice: newPricingType === "CustomPrice" ? prev.dealPrice : "",
                    dealDiscountPercentage: newPricingType === "Discount" ? prev.dealDiscountPercentage : "",
                  }));
                }}
                styles={dropdownStyles}
                placeholder="Fixed Price"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuShouldBlockScroll={false}
                menuShouldScrollIntoView={false}
                closeMenuOnScroll={false}
              />
             
            </div>

            {formState.dealPricingType === "CustomPrice" && (
              <div className="col-md-6">
                <FormInput
                  label={`Deal Price (${getCurrencySymbol(compCurrency)}) *`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formState.dealPrice}
                  onchange={(e) => handleInputChange("dealPrice", e.target.value)}
                  placeHolder="Enter fixed deal price"
                />
              </div>
            )}

            {formState.dealPricingType === "Discount" && (
              <div className="col-md-6">
                <FormInput
                  label="Deal Discount Percentage"
                  type="number"
                  min="0"
                  max="100"
                  value={formState.dealDiscountPercentage}
                  onchange={(e) => handleInputChange("dealDiscountPercentage", e.target.value)}
                  placeHolder="0-100"
                />
              </div>
            )}

            <div className="col-md-12">
              <label className="form-label fw-semibold mb-2">Deal Description</label>
              <textarea
                className="form-control"
                rows="4"
                value={formState.dealDescription}
                onChange={(e) => handleInputChange("dealDescription", e.target.value)}
                placeholder="Enter deal description"
                style={{
                  backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                  border: isDarkMode ? '1px solid #4a5568' : '1px solid #e2e8f0',
                  color: isDarkMode ? '#ffffff' : '#2d3748',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = themeColor;
                  e.target.style.boxShadow = `0 0 0 3px ${themeColor}20`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDarkMode ? '#4a5568' : '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div className="col-md-6">
              <FormInput
                label={`Minimum Order Value (${getCurrencySymbol(compCurrency)})`}
                type="number"
                step="0.01"
                min="0"
                value={formState.minimumOrderValue}
                onchange={(e) => handleInputChange("minimumOrderValue", e.target.value)}
                placeHolder="0.00"
              />
              <small className="text-muted" style={{ color: isDarkMode ? '#a0aec0' : '#718096' }}>
                Minimum order value required to qualify for this deal (leave empty for no minimum)
              </small>
            </div>
            {/* <div className="col-md-12 mt-3">
              <div style={{
                padding: '16px',
                backgroundColor: isDarkMode ? '#2d3436' : '#f7fafc',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #4a5568' : '1px solid #e2e8f0'
              }}>
                <ToggleSwitch
                  label="Auto-duplicate item for BOGO (same item with same modifiers)"
                  isChecked={toggles.autoDuplicateItem || false}
                  onToggle={(checked) => handleToggleChange("autoDuplicateItem", checked)}
                  themeColor={themeColor}
                />
                <small className="text-muted d-block mt-2" style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '12px' }}>
                  When enabled, adding one item to a slot will automatically add the same item with the same modifiers and variant as the free item
                </small>
              </div>
            </div>  */}

            {/* <div className="col-md-6">
              <FormInput
                label="Sort Order"
                type="number"
                min="0"
                value={formState.dealOrder}
                onChange={(e) => handleInputChange("dealOrder", e.target.value)}
                placeholder="0"
              />
            </div> */}

            <div className="col-md-12">
              <div className="row g-4">
                <div className="col-md-6">
                  <label
                    className="form-label fw-semibold mb-2"
                    style={{ color: isDarkMode ? "#f1f5f9" : "#2d3748" }}
                  >
                    Start Date
                  </label>
                  <div style={{
                    padding: '10px',
                    backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                    borderRadius: '10px',
                    border: isDarkMode ? '1px solid #64748b' : '1px solid #e2e8f0'
                  }}>
                    <CustomDateTimePicker
                      value={formState.startDate}
                      onChange={(date) => handleInputChange("startDate", date)}
                      placeholder="Select start date"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <label
                    className="form-label fw-semibold mb-2"
                    style={{ color: isDarkMode ? "#f1f5f9" : "#2d3748" }}
                  >
                    End Date
                  </label>
                  <div style={{
                    padding: '10px',
                    backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                    borderRadius: '10px',
                    border: isDarkMode ? '1px solid #64748b' : '1px solid #e2e8f0'
                  }}>
                    <CustomDateTimePicker
                      value={formState.endDate}
                      onChange={(date) => handleInputChange("endDate", date)}
                      placeholder="Select end date"
                    />
                  </div>
                </div>
              </div>
            </div>

            

            {/* Deal-Level Pricing Configuration */}
            
            <div className="col-md-12 mt-4">
              <h5 style={{ 
                color: isDarkMode ? '#ffffff' : '#2d3748',
                marginBottom: '16px',
                fontWeight: '600'
              }}>
                Deal Schedule Configuration
              </h5>
            </div>

            <div className="col-md-12 mb-3">
              <div style={{
                padding: '16px',
                backgroundColor: isDarkMode ? '#2d3436' : '#f7fafc',
                borderRadius: '12px',
                border: isDarkMode ? '1px solid #4a5568' : '1px solid #e2e8f0'
              }}>
                <ToggleSwitch
                  label="Set schedule"
                  isChecked={toggles.isScheduleDeal || false}
                  onToggle={(checked) => handleToggleChange("isScheduleDeal", checked)}
                  themeColor={themeColor}
                />
                <p className="text-muted mt-2" style={{ color: isDarkMode ? '#a0aec0' : '#718096' }}>
                  Set the days of the week and times of day this deal is available. (Example: Happy Hour 4pm-6pm)
                </p>
              </div>
            </div>

            {toggles.isScheduleDeal && (
              <div className="col-md-12 mb-4" style={{ position: 'relative', zIndex: 1000, overflow: 'visible' }}>
                <div className="mb-4 mt-3">
                  <ToggleSwitch
                    isChecked={useSameTimeSlots}
                    onToggle={() => {
                      setUseSameTimeSlots(!useSameTimeSlots);
                      if (!useSameTimeSlots) {
                        applyCommonTimeSlotToAll();
                      }
                    }}
                    label="Use same time slots for all days"
                    disabled={false}
                    themeColor={themeColor}
                  />
                </div>
                <hr />

                <div style={{ position: 'relative', zIndex: 1000, overflow: 'visible' }}>
                  {useSameTimeSlots ? (
                    <CommonTimeSlotForAllWeek
                      setCommonTimeSlots={setCommonTimeSlot}
                      commonTimeSlots={commonTimeSlot}
                      isProfilePage={false}
                    />
                  ) : (
                    <ScheduleManager
                      schedules={schedules}
                      setSchedules={setSchedules}
                      isProfilePage={false}
                      isDarkMode={isDarkMode}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Deal Slots Section */}
      <div 
        className="card border-0 shadow-sm mb-4"
        style={{
          backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
          border: isDarkMode ? '1px solid #404040' : undefined,
          borderRadius: '16px',
          overflow: 'hidden'
        }}
      >
        <div 
          className="card-header border-0"
          style={{
            backgroundColor: isDarkMode ? '#2d3436' : '#f7fafc',
            padding: '20px 24px',
            borderBottom: `2px solid ${isDarkMode ? '#404040' : '#e2e8f0'}`
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="mb-0" style={{ 
              fontSize: '20px', 
              fontWeight: '600',
              color: isDarkMode ? '#ffffff' : '#2d3748',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="fa fa-list-ul" style={{ color: themeColor }}></i>
              Deal Slots
            </h3>
            <button
              type="button"
              className="btn"
              onClick={addSlotRow}
              style={{
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                color: "white",
                border: "none",
                borderRadius: '10px',
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fa fa-plus"></i>
              Add Slot
            </button>
          </div>
        </div>
        <div className="card-body" style={{ padding: '24px' }}>
          {slots.length === 0 ? (
            <div 
              className="text-center py-5"
              style={{
                backgroundColor: isDarkMode ? '#2d3436' : '#f7fafc',
                borderRadius: '12px',
                border: `2px dashed ${isDarkMode ? '#404040' : '#e2e8f0'}`
              }}
            >
              <i className="fa fa-inbox" style={{ fontSize: '48px', color: isDarkMode ? '#4a5568' : '#cbd5e0', marginBottom: '16px' }}></i>
              <p className="mb-0" style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '16px' }}>
                No slots configured. Click "Add Slot" to create one.
              </p>
            </div>
          ) : (
            <div className="slots-list" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {slots.map((slot, index) => {
                // Skip null/undefined slots
                if (!slot) return null;
                
                return (
                  <div
                    key={slot.slotID || `new-${index}`}
                  className="card"
                  style={{
                    backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                    border: `2px solid ${isDarkMode ? '#404040' : '#e9ecef'}`,
                    borderRadius: '16px',
                    boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = themeColor;
                    e.currentTarget.style.boxShadow = isDarkMode 
                      ? `0 6px 20px ${themeColor}40` 
                      : `0 4px 16px ${themeColor}30`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = isDarkMode ? '#404040' : '#e9ecef';
                    e.currentTarget.style.boxShadow = isDarkMode 
                      ? '0 4px 12px rgba(0,0,0,0.3)' 
                      : '0 2px 8px rgba(0,0,0,0.08)';
                  }}
                >
                  {/* Slot Header */}
                  <div 
                    style={{
                      background: `linear-gradient(135deg, ${themeColor}15 0%, ${themeColor}08 100%)`,
                      padding: '20px 24px',
                      borderBottom: '1px solid ' + (isDarkMode ? '#404040' : '#e9ecef'),
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      const newExpanded = new Set(expandedSlots);
                      if (newExpanded.has(index)) {
                        newExpanded.delete(index);
                      } else {
                        newExpanded.add(index);
                      }
                      setExpandedSlots(newExpanded);
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3" style={{ flex: 1 }}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: isDarkMode ? '#ffffff' : '#000000',
                            padding: '4px 8px',
                            minWidth: 'auto'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const newExpanded = new Set(expandedSlots);
                            if (newExpanded.has(index)) {
                              newExpanded.delete(index);
                            } else {
                              newExpanded.add(index);
                            }
                            setExpandedSlots(newExpanded);
                          }}
                        >
                          <i 
                            className={`fa fa-chevron-${expandedSlots.has(index) ? 'up' : 'down'}`}
                            style={{
                              transition: 'transform 0.3s ease',
                              fontSize: '14px'
                            }}
                          ></i>
                        </button>
                        <div 
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 2px 8px ${themeColor}30`
                          }}
                        >
                          <i className="fa fa-list-alt text-white"></i>
                        </div>
                        <div>
                          <h6 className="mb-1" style={{ 
                            color: isDarkMode ? '#ffffff' : '#000000',
                            fontWeight: '600',
                            fontSize: '1.1rem'
                          }}>
                            {slot.slotName || `New Slot ${index + 1}`}
                          </h6>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span 
                              className="badge" 
                              style={{
                                backgroundColor: '#4299e1',
                                color: 'white',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                fontWeight: '500'
                              }}
                            >
                              <i className="fa fa-sort-numeric-up me-1"></i>
                              Required: {slot.requiredQty || 1}
                            </span>
                            {slot.slotID > 0 && (
                              <span 
                                className="badge d-none" 
                                style={{
                                  backgroundColor: '#17a2b8',
                                  color: 'white',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}
                              >
                                <i className="fa fa-sort me-1"></i>
                                Order: {slot.slotOrder || 0}
                              </span>
                            )}
                            {slot.slotID === 0 && (
                              <span 
                                className="badge" 
                                style={{
                                  backgroundColor: '#ffc107',
                                  color: '#000',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}
                              >
                                <i className="fa fa-edit me-1"></i>
                                New
                              </span>
                            )}
                            {slot.allowedCategories && slot.allowedCategories.length > 0 && (
                              <span 
                                className="badge" 
                                style={{
                                  backgroundColor: '#4299e1',
                                  color: 'white',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}
                              >
                                <i className="fa fa-tags me-1"></i>
                                {slot.allowedCategories.length} Categories
                              </span>
                            )}
                            {slot.allowedItems && slot.allowedItems.length > 0 && (
                              <span 
                                className="badge" 
                                style={{
                                  backgroundColor: '#48bb78',
                                  color: 'white',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}
                              >
                                <i className="fa fa-box me-1"></i>
                                {slot.allowedItems.length} Items
                              </span>
                            )}
                            {slot.constraint && (
                              <span 
                                className="badge d-none" 
                                style={{
                                  backgroundColor: '#ed8936',
                                  color: 'white',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}
                              >
                                <i className="fa fa-cog me-1"></i>
                                Constraints Set
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                        {expandedSlots.has(index) && slot.slotID > 0 && (
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => saveSlot(index)}
                            disabled={slotSaving}
                            style={{
                              background: '#28a745',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              fontWeight: '500',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!slotSaving) {
                                e.target.style.background = '#218838';
                                e.target.style.transform = 'translateY(-1px)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!slotSaving) {
                                e.target.style.background = '#28a745';
                                e.target.style.transform = 'translateY(0)';
                              }
                            }}
                          >
                            <i className="fa fa-save me-1"></i>
                            Save Changes
                          </button>
                        )}
                        {expandedSlots.has(index) && slot.slotID === 0 && (
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => saveSlot(index)}
                            disabled={slotSaving}
                            style={{
                              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              fontWeight: '500',
                              boxShadow: `0 2px 8px ${themeColor}30`,
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!slotSaving) {
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = `0 4px 12px ${themeColor}40`;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!slotSaving) {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = `0 2px 8px ${themeColor}30`;
                              }
                            }}
                          >
                            <i className="fa fa-check me-1"></i>
                            Save Slot
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => removeSlotRow(slot, index)}
                          disabled={slotSaving}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!slotSaving) {
                              e.target.style.background = '#c82333';
                              e.target.style.transform = 'translateY(-1px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!slotSaving) {
                              e.target.style.background = '#dc3545';
                              e.target.style.transform = 'translateY(0)';
                            }
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Slot Body - Accordion Content */}
                  <div 
                    className="card-body p-4"
                    style={{
                      display: expandedSlots.has(index) ? 'block' : 'none',
                      transition: 'all 0.3s ease',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Basic Information Section */}
                    <div 
                      className="mb-4"
                      style={{
                        padding: '20px',
                        backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                      }}
                    >
                      <div className="d-flex align-items-center mb-3">
                        <i className="fa fa-info-circle me-2" style={{ color: themeColor }}></i>
                        <h6 className="mb-0" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
                          Basic Information
                        </h6>
                      </div>
                      <div className="row g-4">
                        <div className="col-12">
                          <FormInput
                            label="Slot Name *"
                            placeHolder="Enter slot name"
                            type="text"
                            value={slot.slotName || ""}
                            onchange={(e) => handleSlotChange(index, "slotName", e.target.value)}
                          />
                        </div>
                        <div className="col-md-6">
                          <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '10px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <ToggleSwitch
                              label="Set Required Quantity"
                              isChecked={slot.requiredQty != null && slot.requiredQty !== ""}
                              onToggle={(checked) => {
                                handleSlotChange(index, "requiredQty", checked ? (parseInt(slot.requiredQty, 10) || 0) : null);
                              }}
                              themeColor={themeColor}
                            />
                            {slot.requiredQty != null && slot.requiredQty !== "" && (
                              <div className="mt-3">
                                <FormInput
                                  label="Required Qty"
                                  placeHolder="0"
                                  type="number"
                                  min="0"
                                  value={slot.requiredQty?.toString() ?? "0"}
                                  onchange={(e) => {
                                    const raw = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                                    const value = isNaN(raw) ? 0 : Math.max(0, raw);
                                    handleSlotChange(index, "requiredQty", value);
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '10px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <ToggleSwitch
                              label="Set Variant Name (applies to all items)"
                              isChecked={slot.selectedVariant != null && (String(slot.selectedVariant).trim() !== "" || String(slot.selectedVariant).length > 0)}
                              onToggle={(checked) => {
                                const current = String(slot.selectedVariant || "").trim();
                                handleSlotChange(index, "selectedVariant", checked ? (current || " ") : null);
                              }}
                              themeColor={themeColor}
                            />
                            {slot.selectedVariant != null && (String(slot.selectedVariant).trim() !== "" || String(slot.selectedVariant).length > 0) && (
                              <div className="mt-3">
                                <FormInput
                                  label="Variant Name"
                                  type="text"
                                  value={(slot.selectedVariant || "").trim()}
                                  onchange={(e) => {
                                    const value = e.target.value.trim();
                                    handleSlotChange(index, "selectedVariant", value === "" ? null : value);
                                  }}
                                  placeHolder="e.g., Large, Medium, Small, Family Size"
                                />
                                <small className="form-text text-muted mt-1" style={{
                                  display: 'block',
                                  color: isDarkMode ? '#a0aec0' : '#6c757d',
                                  fontSize: '12px',
                                  marginTop: '4px'
                                }}>
                                  Matches your item variants. Applies to all items in this slot.
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Slot Pricing Configuration - only when Deal Pricing Type is Slot Pricing */}
                    {formState.dealPricingType === "SlotPricing" && (
                    <div 
                      className="mb-4"
                      style={{
                        padding: '20px',
                        backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                      }}
                    >
                      <div className="d-flex align-items-center mb-3">
                        <i className="fa fa-tag me-2" style={{ color: '#48bb78' }}></i>
                        <h6 className="mb-0" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
                          Slot Pricing Configuration
                        </h6>
                      </div>
                      <div className="row g-4">
                          <>
                        <div className="col-md-6">
                          <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '10px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <label className="form-label fw-semibold mb-2" style={{ color: isDarkMode ? '#ffffff' : '#2d3748' }}>
                              Pricing Type
                            </label>
                            <Select
                              options={[
                                { value: "NoDiscount", label: "No discount on this slot" },
                                { value: "Free", label: "Free" },
                                { value: "Discount", label: "Custom Discount %" },
                                { value: "FixedPricePerItem", label: "Fixed Price (Per Item)" },
                                { value: "FixedPricePerSlot", label: "Fixed Price (Per Slot)" }
                              ]}
                              value={(() => {
                                const pt = slot.pricingType || "NoDiscount";
                                const labels = {
                                  NoDiscount: "No discount on this slot",
                                  Fixed: "No discount on this slot",
                                  Free: "Free",
                                  Discount: "Custom Discount %",
                                  FixedPricePerItem: "Fixed Price (Per Item)",
                                  CustomPrice: "Fixed Price (Per Item)",
                                  FixedPricePerSlot: "Fixed Price (Per Slot)"
                                };
                                const value = pt === "Fixed" ? "NoDiscount" : pt === "CustomPrice" ? "FixedPricePerItem" : pt;
                                return { value, label: labels[pt] || labels.NoDiscount };
                              })()}
                              onChange={(option) => {
                                const newPricingType = option ? option.value : "NoDiscount";
                                handleSlotChange(index, "pricingType", newPricingType);
                                if (newPricingType === "Free") {
                                  handleSlotChange(index, "isFree", true);
                                  handleSlotChange(index, "discountPercentage", null);
                                  handleSlotChange(index, "customPrice", null);
                                } else if (newPricingType === "NoDiscount" || newPricingType === "FixedPricePerItem" || newPricingType === "FixedPricePerSlot") {
                                  handleSlotChange(index, "isFree", false);
                                  handleSlotChange(index, "discountPercentage", null);
                                  if (newPricingType === "NoDiscount") handleSlotChange(index, "customPrice", null);
                                } else if (newPricingType === "Discount") {
                                  handleSlotChange(index, "isFree", false);
                                  handleSlotChange(index, "customPrice", null);
                                }
                              }}
                              styles={dropdownStyles}
                              placeholder="Select pricing type"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                            {(slot.pricingType === "Discount") && (
                              <div className="mt-3">
                                <FormInput
                                  label="Discount Percentage"
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={slot.discountPercentage?.toString() ?? ""}
                                  onchange={(e) => handleSlotChange(index, "discountPercentage", e.target.value ? parseFloat(e.target.value) : null)}
                                  placeHolder="0-100"
                                />
                              </div>
                            )}
                            {(slot.pricingType === "FixedPricePerItem" || slot.pricingType === "CustomPrice") && (
                              <div className="mt-3">
                                <FormInput
                                  label="Fixed Price (Per Item) *"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={slot.customPrice?.toString() ?? ""}
                                  onchange={(e) => handleSlotChange(index, "customPrice", e.target.value && e.target.value.trim() !== "" ? parseFloat(e.target.value) : null)}
                                  placeHolder="0.00"
                                />
                                <small className="text-muted" style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                  Fixed price per item (e.g., 5.00 for £5 each)
                                </small>
                              </div>
                            )}
                            {slot.pricingType === "FixedPricePerSlot" && (
                              <div className="mt-3">
                                <FormInput
                                  label="Fixed Price (Per Slot) *"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={slot.customPrice?.toString() ?? ""}
                                  onchange={(e) => handleSlotChange(index, "customPrice", e.target.value && e.target.value.trim() !== "" ? parseFloat(e.target.value) : null)}
                                  placeHolder="0.00"
                                />
                                <small className="text-muted" style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                  Fixed price for the whole slot (e.g., 10.00 for £10 per slot)
                                </small>
                              </div>
                            )}
                            {slot.pricingType === "Free" && (
                              <div className="mt-3">
                                <ToggleSwitch
                                  label="Auto-duplicate item for BOGO"
                                  isChecked={slot.autoDuplicateItem ?? false}
                                  onToggle={(checked) => handleSlotChange(index, "autoDuplicateItem", checked)}
                                  themeColor={themeColor}
                                />
                                <small className="text-muted" style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                                  When enabled, adding one item will automatically add the same item with the same modifiers
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                        {slot.pricingType !== "NoDiscount" && slot.pricingType !== "Fixed" && slot.pricingType != null && (
                        <div className="col-md-6">
                          <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '10px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <ToggleSwitch
                              label="Set items at full price (rest get discount)"
                              isChecked={slot.buyQuantity != null && slot.buyQuantity !== "" && Number(slot.buyQuantity) > 0}
                              onToggle={(checked) => {
                                handleSlotChange(index, "buyQuantity", checked ? (Math.max(1, parseInt(slot.buyQuantity, 10) || 1)) : null);
                              }}
                              themeColor={themeColor}
                            />
                            {slot.buyQuantity != null && slot.buyQuantity !== "" && Number(slot.buyQuantity) > 0 && (
                              <div className="mt-3">
                                <FormInput
                                  label="Items at full price (rest get discount)"
                                  type="number"
                                  min="1"
                                  value={slot.buyQuantity?.toString() ?? "1"}
                                  onchange={(e) => {
                                    const raw = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                                    const value = isNaN(raw) ? 0 : Math.max(0, raw);
                                    handleSlotChange(index, "buyQuantity", value === 0 ? null : value);
                                  }}
                                  placeHolder="0"
                                />
                                <small className="text-muted" style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                  Out of the required quantity: this many at full price; the rest get the discount. E.g. Required 2, value 1 = 1 full price, 1 discount (BOGO).
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                        )}
                          </>
                      </div>
                    </div>
                    )}

                    {/* Constraints Section - hidden */}
                    <div 
                      className="mb-4 d-none"
                      style={{
                        padding: '20px',
                        backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                      }}
                    >
                      <div className="d-flex align-items-center mb-3">
                        <i className="fa fa-cog me-2" style={{ color: '#ed8936' }}></i>
                        <h6 className="mb-0" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
                          Constraints
                        </h6>
                      </div>
                      <div className="row g-3">
                        <div className="col-md-6 d-none">
                          <FormInput
                            label="Min Qty (Optional)"
                            placeHolder="Leave empty for no limit"
                            type="number"
                            min="1"
                            value={slot.constraint?.minQty?.toString() || ""}
                            onchange={(e) => handleConstraintChange(index, "minQty", e.target.value ? parseInt(e.target.value) : null)}
                          />
                        </div>
                        <div className="col-md-6 d-none">
                          <FormInput
                            label="Max Qty (Optional)"
                            placeHolder="Leave empty for no limit"
                            type="number"
                            min="1"
                            value={slot.constraint?.maxQty?.toString() || ""}
                            onchange={(e) => handleConstraintChange(index, "maxQty", e.target.value ? parseInt(e.target.value) : null)}
                          />
                        </div>
                        <div className="col-md-6">
                          <div style={{
                            padding: '12px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '8px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <ToggleSwitch
                              label="Allow Duplicate Items"
                              isChecked={slot.constraint?.allowDuplicateItems !== false}
                              onToggle={(checked) => handleConstraintChange(index, "allowDuplicateItems", checked)}
                              themeColor={themeColor}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{
                            padding: '12px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '8px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <ToggleSwitch
                              label="Allow Mixed Categories"
                              isChecked={slot.constraint?.allowMixedCategories !== false}
                              onToggle={(checked) => handleConstraintChange(index, "allowMixedCategories", checked)}
                              themeColor={themeColor}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Categories/Items Selection Section */}
                    <div 
                      className="mb-4"
                      style={{
                        padding: '20px',
                        backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                      }}
                    >
                      {/* Categories Section */}
                      {slot.allowedCategories && slot.allowedCategories.length > 0 ? (
                        <div>
                          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                            <h6
                              className="mb-0"
                              style={{
                                color: isDarkMode ? "#ffffff" : "#000000",
                                fontWeight: "600",
                                fontSize: "16px",
                              }}
                            >
                              Selected Categories *
                            </h6>
                            <button
                              type="button"
                              onClick={() => handleManageCategories(slot, index)}
                              style={{
                                background: themeColor,
                                border: "none",
                                borderRadius: "8px",
                                padding: "8px 14px",
                                color: "white",
                                fontSize: "13px",
                                fontWeight: "500",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <i className="fa-solid fa-plus" style={{ fontSize: "11px" }} />
                              Add More
                            </button>
                          </div>
                          <div
                            style={{
                              padding: "12px 14px",
                              backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                              borderRadius: "10px",
                              border: `1px solid ${isDarkMode ? "#4a5568" : "#e2e8f0"}`,
                            }}
                          >
                            <div className="d-flex flex-wrap gap-2">
                              {slot.allowedCategories.map((cat, catIdx) => {
                                const catIDRaw = cat.categoryID || cat.CategoryID;
                                const catID =
                                  catIDRaw !== undefined && catIDRaw !== null
                                    ? parseInt(catIDRaw, 10)
                                    : null;
                                if (!catID) return null;
                                const categoryName =
                                  cat.rootCat_Name ||
                                  cat.RootCat_Name ||
                                  cat.categoryName ||
                                  cat.CategoryName ||
                                  getCategoryName(catID);
                                return (
                                  <span
                                    key={cat.id || cat.ID || catIdx}
                                    className="d-inline-flex align-items-center gap-2"
                                    style={{
                                      backgroundColor: `${themeColor}20`,
                                      color: isDarkMode ? "#ffffff" : "#2d3748",
                                      border: `1px solid ${themeColor}55`,
                                      borderRadius: "999px",
                                      padding: "6px 10px 6px 12px",
                                      fontSize: "13px",
                                      fontWeight: "500",
                                      maxWidth: "100%",
                                    }}
                                    title={categoryName}
                                  >
                                    <i
                                      className="fa fa-tag"
                                      style={{ color: themeColor, fontSize: "11px" }}
                                    />
                                    <span
                                      style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: "220px",
                                      }}
                                    >
                                      {categoryName}
                                    </span>
                                    <button
                                      type="button"
                                      aria-label={`Remove ${categoryName}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCategory(catID, slot.slotID);
                                      }}
                                      style={{
                                        background: "transparent",
                                        border: "none",
                                        color: "#dc3545",
                                        width: "22px",
                                        height: "22px",
                                        borderRadius: "50%",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        padding: 0,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <i className="fa fa-times" style={{ fontSize: "11px" }} />
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ) : slot.allowedItems && slot.allowedItems.length > 0 ? (
                        <div>
                          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                            <h6
                              className="mb-0"
                              style={{
                                color: isDarkMode ? "#ffffff" : "#000000",
                                fontWeight: "600",
                                fontSize: "16px",
                              }}
                            >
                              Selected Items *
                            </h6>
                            <button
                              type="button"
                              onClick={() => handleManageItems(slot, index)}
                              style={{
                                background: themeColor,
                                border: "none",
                                borderRadius: "8px",
                                padding: "8px 14px",
                                color: "white",
                                fontSize: "13px",
                                fontWeight: "500",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <i className="fa-solid fa-plus" style={{ fontSize: "11px" }} />
                              Add More
                            </button>
                          </div>
                          <div
                            style={{
                              padding: "12px 14px",
                              backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                              borderRadius: "10px",
                              border: `1px solid ${isDarkMode ? "#4a5568" : "#e2e8f0"}`,
                            }}
                          >
                            <div className="d-flex flex-wrap gap-2">
                              {slot.allowedItems.map((item, itemIdx) => {
                                const itemIDRaw =
                                  typeof item === "object"
                                    ? (item.itemID || item.ItemID || item.menuItem_ID || item.MenuItem_ID || item.id)
                                    : item;
                                const itemID =
                                  itemIDRaw !== undefined && itemIDRaw !== null
                                    ? parseInt(itemIDRaw, 10)
                                    : null;
                                if (!itemID) return null;
                                const itemName =
                                  (typeof item === "object" &&
                                    (item.menuItem_Name || item.MenuItem_Name || item.name)) ||
                                  getItemName(itemID);
                                return (
                                  <span
                                    key={item.id || item.ID || itemIdx}
                                    className="d-inline-flex align-items-center gap-2"
                                    style={{
                                      backgroundColor: `${themeColor}20`,
                                      color: isDarkMode ? "#ffffff" : "#2d3748",
                                      border: `1px solid ${themeColor}55`,
                                      borderRadius: "999px",
                                      padding: "6px 10px 6px 12px",
                                      fontSize: "13px",
                                      fontWeight: "500",
                                      maxWidth: "100%",
                                    }}
                                    title={itemName}
                                  >
                                    <i
                                      className="fa fa-box"
                                      style={{ color: themeColor, fontSize: "11px" }}
                                    />
                                    <span
                                      style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: "220px",
                                      }}
                                    >
                                      {itemName}
                                    </span>
                                    <button
                                      type="button"
                                      aria-label={`Remove ${itemName}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteItem(itemID, slot.slotID);
                                      }}
                                      style={{
                                        background: "transparent",
                                        border: "none",
                                        color: "#dc3545",
                                        width: "22px",
                                        height: "22px",
                                        borderRadius: "50%",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        padding: 0,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <i className="fa fa-times" style={{ fontSize: "11px" }} />
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                      ) : (
                        <div>
                          <div className="d-flex align-items-center mb-3">
                            <h6 
                              className="mb-0" 
                              style={{ 
                                color: isDarkMode ? '#ffffff' : '#000000', 
                                fontWeight: '600',
                                fontSize: '16px'
                              }}
                            >
                              Select Categories or Items
                            </h6>
                          </div>
                          <div className="text-center py-4">
                            <div 
                              style={{
                                width: '60px',
                                height: '60px',
                                background: isDarkMode ? '#555' : '#f8f9fa',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px',
                                border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                              }}
                            >
                              <i className="fa fa-info-circle" style={{ fontSize: '24px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
                            </div>
                            <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', fontSize: '15px', marginBottom: '20px' }}>No selection type chosen. Please select Categories or Items.</p>
                            <div className="d-flex gap-2 justify-content-center">
                              <button
                                onClick={() => {
                                  selectedSlotRef.current = slot;
                                  selectedSlotIndexRef.current = index;
                                  setSelectedSlot(slot);
                                  handleTypeSelection('categories');
                                }}
                                style={{
                                  background: '#4299e1',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '10px 20px',
                                  color: 'white',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#3182ce';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#4299e1';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <i className="fa fa-tags me-1"></i>
                                Select Categories
                              </button>
                              <button
                                onClick={() => {
                                  selectedSlotRef.current = slot;
                                  selectedSlotIndexRef.current = index;
                                  setSelectedSlot(slot);
                                  handleTypeSelection('items');
                                }}
                                style={{
                                  background: '#48bb78',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '10px 20px',
                                  color: 'white',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#38a169';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#48bb78';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <i className="fa fa-box me-1"></i>
                                Select Items
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {!isChildBranch && dealId && CompID && (
        <DealBranchAssignmentPanel
          dealId={dealId}
          parentCompId={CompID}
          currencySymbol={getCurrencySymbol(compCurrency)}
          dealName={formState.dealName}
        />
      )}
      </GridSectionLoader>

      {/* Type Selection Modal */}
      <DealSlotTypeSelectionModal
        isOpen={showTypeSelectionModal}
        onClose={() => {
          setShowTypeSelectionModal(false);
          // Don't clear slot if we're transitioning to category/item modal
          // Check if type selection is in progress (handleTypeSelection was just called)
          // or if modals are already open
          if (!typeSelectionInProgressRef.current) {
            // User closed without selecting - clear the slot
            // But wait a bit to ensure handleTypeSelection had time to run if it was called
            setTimeout(() => {
              // Double check - if type selection didn't happen and modals didn't open, clear
              if (!typeSelectionInProgressRef.current && !showCategoryModal && !showItemModal) {
                setSelectedSlot(null);
                selectedSlotRef.current = null;
                selectedSlotIndexRef.current = null;
                setSelectedType(null);
              }
            }, 150);
          }
          // If typeSelectionInProgressRef is true, handleTypeSelection was called
          // and will open the modal - don't clear the slot
        }}
        onSelectType={handleTypeSelection}
      />

      {/* Category Selection Modal */}
      <CategorySelector
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        showModal={showCategoryModal}
        setShowModal={(val) => {
          setShowCategoryModal(val);
          // Only clear selectedSlot if modal is closed without saving (e.g., clicking X)
          // Don't clear here when "Done" is clicked - handleSaveCategories will handle it
          if (!val) {
            // Reset type selection flag when modal closes
            typeSelectionInProgressRef.current = false;
            // Wait a bit to see if save handler clears it
            setTimeout(() => {
              // Only clear if still not null (meaning save didn't run or didn't clear it)
              if (selectedSlotRef.current || selectedSlot) {
                selectedSlotRef.current = null;
                setSelectedSlot(null);
                setSelectedType(null);
                selectedSlotIndexRef.current = null;
              }
            }, 2000);
          }
        }}
        onModalSubmit={handleSaveCategories}
        setLoading={setCategoryModalLoading}
        hideDisplay={true}
      />

      {/* Item Selection Modal */}
      <ProductMultiSelectionModal
        showModal={showItemModal}
        setShowModal={(value) => {
          setShowItemModal(value);
          // Only clear selectedSlot if modal is closed without saving (e.g., clicking X)
          // Don't clear here when "Done" is clicked - handleSaveItems will handle it
          if (!value) {
            // Reset type selection flag when modal closes
            typeSelectionInProgressRef.current = false;
            // Wait a bit to see if save handler clears it
            setTimeout(() => {
              // Only clear if still not null (meaning save didn't run or didn't clear it)
              if (selectedSlotRef.current || selectedSlot) {
                selectedSlotRef.current = null;
                setSelectedSlot(null);
                setSelectedType(null);
                selectedSlotIndexRef.current = null;
              }
            }, 2000);
          }
        }}
        showButton={true}
        onSubmit={handleSaveItems}
        setSelectedProducts={setSelectedItems}
        selectedProducts={selectedItems}
        products={allItems}
      />

    </div>
  );
};

export default UpdateDeal;
